import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getFertilizerDictionaryRequest,
} from '../requests/fertilizersRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  deleteDisabled?: boolean;
}

const FertilizersDictionary = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');
  const [tableKey, setTableKey] = useState(0);
  const resetFilters = () => {
    setTableKey((tableKey) => tableKey + 1);
    setSearchedColumn('');
  };

  const downloadList = () =>
    getFertilizerDictionaryRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadList();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Nazwa produktu',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po nazwie',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn,
        resetFilters
      ),
      render: (_: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`nawozy/${record.categoryUrl}/${record.subCategoryUrl}/${record.url}`}
        />
      ),
    },
    {
      title: 'Ilość słowników',
      dataIndex: 'dictionaryCount',
      key: 'dictionaryCount',
      width: 160,
      sorter: (
        a: { dictionaryCount: number },
        b: { dictionaryCount: number }
      ) => a.dictionaryCount - b.dictionaryCount,
    },
    {
      title: 'Kategoria',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      sorter: (a: { category: number }, b: { category: number }) =>
        a.category - b.category,
      ...useColumnSearch(
        'categoryName',
        'Szukaj po kategorii',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn,
        resetFilters
      ),
      render: (_: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.CATEGORY_TAG}
          fieldName="categoryName"
        />
      ),
    },
    {
      title: 'Podkategoria',
      dataIndex: 'subCatName',
      key: 'subCatName',
      width: 160,
      sorter: (a: { subCatName: string }, b: { subCatName: string }) =>
        a?.subCatName?.localeCompare(b?.subCatName),
      render: (_: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.CATEGORY_TAG}
          fieldName="subCatName"
        />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '100px',
      render: (_: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (_: string, item: NewsTableTypes) => {
        return (
          <Space
            size="middle"
            style={{ display: 'Flex', flexDirection: 'column' }}
          >
            <Tooltip title="Edytuj wpis">
              <Link to={`/fertilizers-dictionary/${item.id}`}>
                <Button type="primary" shape="circle" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Nawozy</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilizers-dictionary">Słowniki</Link>
          </Breadcrumb.Item>
        </>
      </Bread>

      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
          key={tableKey}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default FertilizersDictionary;
